import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/options";
import { RcButton } from "@/components/RcButton";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", info: null });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", info: "Missing session" }); return; }
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", info: data });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", info: data });
          return;
        }
        if (attempts >= 6) {
          setState({ status: "pending", info: data });
          return;
        }
        setTimeout(tick, 2000);
      } catch {
        setState({ status: "error", info: "Could not verify payment" });
      }
    };
    tick();
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center">
      {state.status === "polling" && (
        <>
          <Loader2 className="w-12 h-12 text-[#CFA05E] animate-spin mx-auto mb-6" strokeWidth={1.4} />
          <h2 className="font-display text-4xl mb-3">Confirming your order…</h2>
          <p className="text-[#A39E93]">Please hold while we verify the payment.</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 text-[#CFA05E] mx-auto mb-6" strokeWidth={1.2} />
          <h2 className="font-display text-5xl mb-3">Order confirmed.</h2>
          <p className="text-[#A39E93] mb-2">Thank you. Your bespoke garment is now in our atelier.</p>
          <p className="font-display text-3xl text-[#CFA05E] mb-10" data-testid="paid-amount">{formatINR(state.info.amount)}</p>
          <div className="flex justify-center gap-4">
            <Link to="/designs"><RcButton variant="outline">View My Designs</RcButton></Link>
            <Link to="/designer"><RcButton>Design Another</RcButton></Link>
          </div>
        </>
      )}
      {(state.status === "expired" || state.status === "error" || state.status === "pending") && (
        <>
          <AlertCircle className="w-14 h-14 text-[#6B2737] mx-auto mb-6" strokeWidth={1.2} />
          <h2 className="font-display text-4xl mb-3">
            {state.status === "expired" ? "Session expired" : state.status === "pending" ? "Still processing" : "Something went wrong"}
          </h2>
          <p className="text-[#A39E93] mb-10">
            {state.status === "pending"
              ? "We're still confirming your payment. Check My Designs in a moment."
              : "Please try again, or contact our atelier."}
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/designs"><RcButton variant="outline">My Designs</RcButton></Link>
            <Link to="/designer"><RcButton>Back to Designer</RcButton></Link>
          </div>
        </>
      )}
    </div>
  );
}
